import axios, { AxiosResponse } from 'axios';
import {
  CloseUser,
  CrRefundFormData,
  DuplicateUserRes,
  EditBalanceFormData,
  LeelooAccounts,
  UserSearchItem,
  UpdateUserReq,
  UserDetailQuery,
  UserNote,
  UserProfile,
  UserReq,
  UserRes,
  PaymentReq,
  UserPayment,
  UserNotesResponse,
} from '@src/types/users.ts';
import { ConsentResponse, InvoicesResponse } from '@src/types/invoices';

export const getUsers = (params?: UserReq): Promise<AxiosResponse<UserRes>> => {
  return axios.get('/leeloo-users/', { params: { ...params, size: 20 } });
};

export const getBrowseUsers = (
  params?: UserReq
): Promise<AxiosResponse<any>> => {
  return axios.get('/users/', { params: { ...params, size: 20 } });
};

export const getMyTradingAccounts = (): Promise<
  AxiosResponse<{
    created_at: string;
    id: number;
    username: string;
    accounts: LeelooAccounts[];
    competition_activation_date: string | null;
  }>
> => {
  return axios.get('/leeloo-users/my-accounts');
};

export const getNotes = (
  id: number,
  searchWords?: string
): Promise<AxiosResponse<UserNote[]>> => {
  const params = { searchwords: searchWords };

  return axios.get(`/leeloo-users/${id}/notes/`, { params });
};

export const addNote = (
  id: number,
  content: string
): Promise<AxiosResponse<UserNote>> => {
  return axios.post(`/leeloo-users/${id}/notes/`, {
    content,
  });
};

export const updateNote = (
  id: number,
  noteId: number,
  content: string
): Promise<AxiosResponse<UserNote>> => {
  return axios.put(`/leeloo-users/${id}/notes/${noteId}/`, {
    content,
  });
};

export const deleteNote = (
  id: number,
  noteId: number
): Promise<AxiosResponse<void>> => {
  return axios.delete(`/leeloo-users/${id}/notes/${noteId}/`);
};

export const getUserDetail = (
  id: number,
  params?: UserDetailQuery
): Promise<AxiosResponse<UserProfile>> => {
  return axios.get(`/leeloo-users/${id}/`, { params });
};
export const getBrowseUserDetail = (
  id: number,
  params?: UserDetailQuery
): Promise<AxiosResponse<UserProfile>> => {
  return axios.get(`/users/${id}/`, { params });
};

export const getAccountActivities = (
  id: number,
  params?: UserDetailQuery
): Promise<AxiosResponse<LeelooAccounts[]>> => {
  return axios.get(`/leeloo-users/${id}/account-activities`, { params });
};

export const updateUser = (
  id: number,
  payload: UpdateUserReq
): Promise<AxiosResponse<UserProfile>> => {
  return axios.put(`/leeloo-users/${id}/`, payload);
};

export const updateBrowseUser = (
  id: number,
  payload: UpdateUserReq
): Promise<AxiosResponse<UserProfile>> => {
  return axios.put(`/users/${id}/`, payload);
};

export const resendSignUpEmail = (
  id: number
): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(`/users/${id}/resend-signup-email/`);
};

export const getUserPayments = (
  id: number,
  params: PaymentReq
): Promise<AxiosResponse<UserPayment>> => {
  return axios.get(`/users/${id}/payments`, { params });
};

export const getDuplicateUser = (
  params?: UserReq
): Promise<AxiosResponse<DuplicateUserRes[]>> => {
  return axios.get(`/leeloo-users/duplicates/`, {
    params,
  });
};

export const exportPhonesDuplicateUser = (
  params?: UserReq
): Promise<AxiosResponse<BlobPart>> => {
  return axios.get(`/leeloo-users/duplicates/phones/export`, {
    params,
    responseType: 'blob',
  });
};

export const exportAddressDuplicateUser = (
  params?: UserReq
): Promise<AxiosResponse<BlobPart>> => {
  return axios.get(`/leeloo-users/duplicates/phones/export`, {
    params,
    responseType: 'blob',
  });
};

export const flagUser = (id: number): Promise<AxiosResponse<boolean>> => {
  return axios.post(`/leeloo-users/${id}/flag/`);
};

export const deactivateUser = (
  id: number
): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(`/leeloo-users/${id}/deactivate/`);
};

export const updateRithmic = (
  id: number
): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(`/leeloo-users/${id}/update-rithmic/`);
};

export const resendWelcomeEmail = (
  id: number
): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(`/leeloo-users/${id}/resend-welcome-email/`);
};

export const deleteUser = (
  id: number
): Promise<AxiosResponse<{ message: string }>> => {
  return axios.delete(`/leeloo-users/${id}/`);
};

export const deactivateUsers = (
  ids: number[]
): Promise<AxiosResponse<boolean>> => {
  return axios.post(`/leeloo-users/bulk-deactivate/`, {
    leeloo_user_ids: ids,
  });
};

export const expiryDateUsers = (
  ids: number[]
): Promise<AxiosResponse<boolean>> => {
  return axios.post(`/leeloo-users/bulk-expiry-date/`, {
    leeloo_user_ids: ids,
  });
};

export const creditUser = (
  id: number,
  accountId: number,
  payload: CrRefundFormData
): Promise<AxiosResponse<boolean>> => {
  return axios.put(
    `/leeloo-users/${id}/accounts/${accountId}/credits/`,
    payload
  );
};

export const editUserBalance = (
  id: number,
  accountId: number,
  payload: EditBalanceFormData
): Promise<AxiosResponse<boolean>> => {
  const newPayload = {
    new_balance: payload.new_balance,
    extra_trading_days: payload.extra_trading_days ?? 0,
    notes: payload.notes,
  };

  return axios.put(
    `/leeloo-users/${id}/accounts/${accountId}/balance/`,
    newPayload
  );
};

export const leelooCloseAccount = (params: {
  id: number;
  accountId: number;
}): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(
    `/leeloo-users/${params.id}/accounts/${params.accountId}/close/`
  );
};

export const bulkLeelooCloseAccount = (params: {
  id: number;
  accounts: CloseUser[];
}): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(`/leeloo-users/${params.id}/accounts/bulk-close/`, {
    accounts: params.accounts,
  });
};

export const assignAccount = (
  id: number,
  accountId: number
): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(`/leeloo-users/${id}/accounts/${accountId}/assign/`);
};

export const reEnableEvalToPa = (
  id: number,
  accountId: number
): Promise<AxiosResponse<{ message: string }>> => {
  return axios.post(
    `/leeloo-users/${id}/accounts/${accountId}/re-enable-eval-to-pa/`
  );
};

// Search leeloo users for suggestions
export const searchUsers = (
  search_words: string
): Promise<AxiosResponse<UserSearchItem[]>> => {
  return axios.get(`/leeloo-users/search`, {
    params: { search_words },
  });
};

export const addPackageToUser = (data: {
  product_id: number;
  leeloo_user_id: number;
  valid_from: string;
  valid_to: string;
  reason?: string;
  send_mail_new_account?: boolean;
}): Promise<AxiosResponse<any>> => {
  return axios.post(`/leeloo-user-packages/`, data);
};

export const savePaymentMethod = (
  leelooUserId: number,
  payload: {
    allow_riseworks: boolean;
    allow_wire: boolean;
    allow_paypal: boolean;
    allow_coupon: boolean;
  }
): Promise<AxiosResponse<UserProfile>> => {
  return axios.post(
    `/leeloo-users/${leelooUserId}/save-payment-method/`,
    payload
  );
};

export const inviteRiseworks = (
  leelooUserId: number
): Promise<AxiosResponse<any>> => {
  return axios.post(`/leeloo-users/invite-riseworks/`, {
    leeloo_user_id: leelooUserId,
  });
};

export const getUserInvoices = (
  userId: number
): Promise<AxiosResponse<InvoicesResponse>> => {
  return axios.get(`/users/${userId}/invoices`);
};

export const getUserConsent = (
  userId: number
): Promise<AxiosResponse<ConsentResponse>> => {
  return axios.get(`/users/${userId}/consent`);
};

// Example value for UserNote
// {
//   "notes": [
//   {
//     "id": 0,
//     "admin_id": 0,
//     "user_id": 0,
//     "content": "string",
//     "attachments": "string",
//     "created_at": "2026-03-23T14:32:25.675Z",
//     "updated_at": "2026-03-23T14:32:25.675Z"
//   }
// ],
//   "total": 0
// }
export const getUserNotes = (
  userId: number,
  keyword?: string
): Promise<AxiosResponse<UserNotesResponse>> => {
  return axios.get(`/users/${userId}/notes`, {
    params: {
      keyword,
    },
  });
};

export const createUserNote = (
  userId: number,
  content: string
): Promise<AxiosResponse<any>> => {
  return axios.post(`/users/${userId}/notes`, {
    content,
  });
};

export const updateUserNote = (
  userId: number,
  noteId: number,
  content: string
): Promise<AxiosResponse<any>> => {
  return axios.put(`/users/${userId}/notes/${noteId}`, {
    content,
  });
};
